class MainHeader {
	header: HTMLElement | null;
	headerNav: HTMLElement | null;
	megaMenuItems: NodeListOf<Element> | null;
	megaMenuBackBtns: NodeListOf<HTMLButtonElement>;
	mobileMenuTrigger: HTMLButtonElement | null;
	adminBar: HTMLElement | null;
	isMobile: boolean;
	isTouch: boolean;
	breakpoints: IBreakpoints | undefined;
	headerHeightFull: number;
	openMenuTimers: Map<HTMLElement, number> = new Map();
	closeMenuTimers: Map<HTMLElement, number> = new Map();

	constructor() {
		this.header = document.querySelector<HTMLElement>('.main-header');
		this.headerNav = this.header?.querySelector<HTMLElement>('.main-header__nav') || null;
		this.adminBar = document.getElementById('wpadminbar');
		this.megaMenuItems =
			this.headerNav?.querySelectorAll(
				'.header-nav-menu__nav-item--has-mm-dropdown, .header-nav-menu__nav-item--has-dropdown'
			) || null;
		this.megaMenuBackBtns = document.querySelectorAll<HTMLButtonElement>(
			'button[data-mega-menu-back]'
		);
		this.mobileMenuTrigger = document.querySelector<HTMLButtonElement>('.mobile-menu-trigger');
		this.breakpoints = window.impBreakpoints;
		this.isMobile = this.isMobileViewport();
		this.isTouch = this.isTouchDevice();
		this.headerHeightFull = 0;
	}

	init() {
		this.setHeaderHeight();

		if (this.megaMenuItems) {
			this.megaMenuItems.forEach((item) => {
				item.addEventListener(
					'mouseenter',
					this.handleMouseEnter.bind(this) as EventListener
				);
				item.addEventListener(
					'mouseleave',
					this.handleMouseLeave.bind(this) as EventListener
				);
				item
					.querySelector('a')
					?.addEventListener('click', this.handleMegaMenuClick.bind(this));
			});
		}

		if (this.mobileMenuTrigger) {
			this.mobileMenuTrigger.addEventListener('click', this.handleMobileMenuClick.bind(this));
		}

		this.megaMenuBackBtns.forEach((item) => {
			item.addEventListener('click', this.handleBackBtnClick.bind(this));
		});
	}

	resized() {
		this.setHeaderHeight();
		this.isMobile = this.isMobileViewport();
		this.isTouch = this.isTouchDevice();
	}

	loaded() {
		this.setHeaderHeight();
		this.isMobile = this.isMobileViewport();
		this.isTouch = this.isTouchDevice();
	}

	handleMobileMenuClick() {
		if (this.isMobile) {
			if (!document.body.classList.contains('mobile-menu-open')) {
				document.body.classList.add('mobile-menu-open');
				document.body.classList.remove('mobile-mm-open');
				this.handleBackBtnClick();
			} else {
				document.body.classList.remove('mobile-menu-open');
				document.body.classList.remove('mobile-mm-open');
				this.megaMenuBackBtns.forEach((btn) => {
					btn.classList.remove('is-open');
				});
			}
		}
	}

	handleBackBtnClick() {
		const activeMegaMenu = document.querySelector<HTMLElement>(
			'.header-nav-menu__nav-item.active'
		);

		if (null !== activeMegaMenu) {
			activeMegaMenu.classList.remove('active');
			this.closeMobileMegaMenu(activeMegaMenu);
		}
	}

	handleMouseEnter(event: MouseEvent) {
		if (this.isMobile || this.isTouch) return;

		const target = event.currentTarget as HTMLElement;

		// If there's a close timer set, user is coming back in – clear that so it won't close.
		const existingCloseTimer = this.closeMenuTimers.get(target);
		if (existingCloseTimer) {
			clearTimeout(existingCloseTimer);
			this.closeMenuTimers.delete(target);
		}

		// If the menu is already open, do nothing.
		const isAlreadyOpen = !!target.querySelector<HTMLElement>(
			'.header-nav-menu__nav-link.is-open'
		);
		if (isAlreadyOpen) return;

		// Start an open timer to ensure enter intent.
		const openTimerId = window.setTimeout(() => {
			document.body.classList.add('dropdown-open');
			this.openMegaMenu(target);
			this.openMenuTimers.delete(target);
		}, 200);

		this.openMenuTimers.set(target, openTimerId);
	}

	handleMouseLeave(event: MouseEvent) {
		if (this.isMobile || this.isTouch) return;

		const target = event.currentTarget as HTMLElement;

		// If there's an open timer running, it means the user left before it opened – clear it.
		const openTimerId = this.openMenuTimers.get(target);
		if (openTimerId) {
			clearTimeout(openTimerId);
			this.openMenuTimers.delete(target);
		}

		// Start a close timer to ensure leave intent.
		const closeTimerId = window.setTimeout(() => {
			document.body.classList.remove('dropdown-open');
			this.closeMegaMenu(target);
			this.closeMenuTimers.delete(target);
		}, 200);

		this.closeMenuTimers.set(target, closeTimerId);
	}

	openMegaMenu(target: HTMLElement) {
		if (this.isMobile) return;

		const megaMenu = target.querySelector<HTMLElement>(
			'.mm-dropdown, .header-nav-menu__dropdown'
		);
		const navLink = target.querySelector<HTMLAnchorElement>('.header-nav-menu__nav-link');
		if (megaMenu) {
			if (megaMenu && navLink) {
				navLink.classList.add('is-open');
				megaMenu.classList.add('is-open');
			}
		}

		// Set the overlay height to cover the screen except for the nav menu
		const overlay = document.querySelector<HTMLElement>('.mega-menu-overlay');
		if (overlay) {
			overlay.style.height = `${window.innerHeight - this.headerHeightFull - (megaMenu?.offsetHeight || 0)}px`;
		}
		document.body.classList.add('mega-menu-has-overlay');
	}

	closeMegaMenu(target: HTMLElement, removeOverlay = true) {
		if (this.isMobile) return;

		if (removeOverlay) {
			document.body.classList.remove('mega-menu-has-overlay');
		}

		const megaMenu = target.querySelector<HTMLElement>(
			'.mm-dropdown, .header-nav-menu__dropdown'
		);
		const navLink = target.querySelector<HTMLAnchorElement>('.header-nav-menu__nav-link');
		if (megaMenu && navLink) {
			navLink.classList.remove('is-open');
			megaMenu.classList.remove('is-open');
		}
	}

	handleMegaMenuClick(event: MouseEvent) {
		event.preventDefault();

		// If desktop and not touch, exit early.
		if (!this.isTouch && !this.isMobile) return;

		const target = event.currentTarget as HTMLAnchorElement;
		const parent = target.parentElement as HTMLElement;
		if (!parent) return;

		if (this.isMobile) {
			if (parent.classList.contains('active')) {
				parent.classList.remove('active');

				this.closeMobileMegaMenu(target, true);
			} else {
				parent.classList.add('active');

				this.openMobileMegaMenu(target, true);
			}
		} else if (target.classList.contains('is-open')) {
			// Close menu
			this.closeMegaMenu(parent);
		} else {
			// Close other open menu if exists
			const openMenuItem = document.querySelector<HTMLAnchorElement>(
				'.header-nav-menu__nav-link--depth-1.is-open'
			);

			if (openMenuItem && openMenuItem.parentElement) {
				this.closeMegaMenu(openMenuItem.parentElement, false);
			}

			// Open Menu
			this.openMegaMenu(parent);
		}
	}

	openMobileMegaMenu(item: HTMLAnchorElement | HTMLElement, isLink = false) {
		const li = isLink ? item.parentElement : item;
		if (!li) return;
		const megaMenu = li.querySelector<HTMLElement>('.mm-dropdown, .header-nav-menu__dropdown');
		if (megaMenu) {
			megaMenu.classList.add('active');
			document.body.classList.add('mobile-mm-open');
			this.megaMenuBackBtns.forEach((btn) => {
				btn.classList.add('is-open');
			});
		}
	}

	closeMobileMegaMenu(item: HTMLAnchorElement | HTMLElement, isLink = false) {
		const li = isLink ? item.parentElement : item;
		if (!li) return;
		const megaMenu = li.querySelector<HTMLElement>('.mm-dropdown, .header-nav-menu__dropdown');
		if (megaMenu) {
			document.body.classList.add('mm-closing');
			megaMenu.classList.add('is-closing');
			megaMenu.classList.remove('active');
			this.megaMenuBackBtns.forEach((btn) => {
				btn.classList.remove('is-open');
			});
			document.body.classList.remove('mobile-mm-open');

			setTimeout(() => {
				document.body.classList.remove('mm-closing');
				megaMenu.classList.remove('is-closing');
			}, 300);
		}
	}

	setHeaderHeight() {
		if (!this.header) return;

		const headerHeight = this.header.offsetHeight;
		let headerHeightFull = this.header.offsetHeight;

		if (null !== this.adminBar) {
			headerHeightFull += this.adminBar.offsetHeight;
		}

		document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
		document.documentElement.style.setProperty('--headerHeight', headerHeight + 'px');
		document.documentElement.style.setProperty('--headerHeightFull', headerHeightFull + 'px');

		this.headerHeightFull = headerHeightFull;
	}

	isTouchDevice(): boolean {
		const userAgent = navigator.userAgent || navigator.vendor;
		return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
			userAgent.toLowerCase()
		);
	}

	isMobileViewport(): boolean {
		const mediaQuery = this.breakpoints
			? `(max-width: ${parseInt(this.breakpoints.lg) - 1}px)`
			: '(max-width: 991px)';
		return window.matchMedia(mediaQuery).matches;
	}
}

export default new MainHeader();
